import React from "react";
import { PATHS } from "../../../config/permissionsPaths";
import useAuth from "../../../hooks/useAuth";
import { Card, CardText, CardsRow, CardTextContainer, ScrollableGroupList } from "../Home.styles";
import { BtnGeneral } from "../../../styles/Buttons";
import PacienteFigura from "./PacienteFigura";
import { calcularProgreso } from "../../../utils/progresUtils";
import icons from "../../../assets/icons/iconMap";

const PatientInfoCards = ({ patientData, yearOldData, resultData, GruposData, openModal }) => {
  const { hasPermission } = useAuth();

  // Función para formatear la edad de manera legible
  const formatAge = () => {
    if (!yearOldData) return "";

    const { anios, meses, dias } = yearOldData;

    if (anios === 0 && meses < 1) {
      return ` ${dias} ${dias === 1 ? 'Día' : 'Días'}`;
    } else if (anios === 0) {
      return ` ${meses} ${meses === 1 ? 'Mes' : 'Meses'}`;
    } else if (anios <= 2) {
      return ` ${anios} ${anios === 1 ? 'Año' : 'Años'} ${meses} ${meses === 1 ? 'Mes' : 'Meses'}`;
    } else {
      return ` ${anios} ${anios === 1 ? 'Año' : 'Años'}`;
    }
  };

  // Función para calcular semanas de embarazo
  const calcularSemanasEmbarazo = (fechaInicio) => {
    if (!fechaInicio || fechaInicio === '1900-01-01') return null;
    const fechaInicioDate = new Date(fechaInicio);
    const hoy = new Date();
    const diffTime = Math.abs(hoy - fechaInicioDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const semanas = Math.floor(diffDays / 7);
    return semanas;
  };

  // Función para formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha || fecha === '1900-01-01') return null;
    const date = new Date(fecha);
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const anio = date.getFullYear();
    return `${dia}/${mes}/${anio}`;
  };

  // Función para agrupar grupos activos e inactivos
  const gruposActivos = GruposData.filter(grupo => Number(grupo?.activo) === 1);
  const gruposInactivos = GruposData.filter(grupo => Number(grupo?.activo) !== 1);

  // console.log(GruposData);

  return (
    <CardsRow>
      {/* CARD 1: Información Personal y Progreso */}
      <Card>
        <CardTextContainer>
          <CardText>
            <strong>{patientData.fullName}</strong>
          </CardText>
          <CardText> <> <strong>Fecha de Nto:</strong> {patientData?.Fecha_Nto} </> </CardText>
          <CardText>
            <>
              <strong>Edad:</strong>{formatAge()}{"  "}
              <strong>Sexo:</strong> {patientData?.Sexo === "M" ? "Masculino" : "Femenino"}
            </>
          </CardText>
          {/* Fecha de inicio de embarazo */}
          {(patientData.tieneSubgrupo9 || patientData.tieneSubgrupo54) && (
            <CardText>
              <strong>Fecha última regla:</strong>{" "}
              {patientData.tieneSubgrupo54 ? (
                "No registra"
              ) : (
                <>
                  {patientData.fechaGestacion || "No registra"}
                  {(() => {
                    const semanas = calcularSemanasEmbarazo(patientData.fechaGestacion);
                    if (semanas === null) return null;
                    const esError = semanas > 42;
                    return (
                      <span style={{ color: esError ? "#dc3545" : "inherit" }}>
                        {" "}
                        ({semanas} sem{esError ? " Error" : ""})
                      </span>
                    );
                  })()}
                </>
              )}
              {patientData.tieneSubgrupo9 && patientData.autoid && (
                <BtnGeneral
                  bg="blue"
                  padding="2px 8px"
                  fs="11px"
                  fw="600"
                  radius="4px"
                  dp="inline-flex"
                  mgl="8px"
                  style={{ verticalAlign: "middle" }}
                  title="Ver cumplimiento de la ruta materna"
                  onClick={() => openModal("rutaMaterna")}
                >
                  Ruta materna
                </BtnGeneral>
              )}
            </CardText>
          )}
          <CardText> <> <strong>EAPB:</strong> {patientData?.Entidad || "No registra"} </> </CardText>
        </CardTextContainer>

        <PacienteFigura
          genero={patientData?.Sexo}
          porcentaje={calcularProgreso(resultData)}
        />
      </Card>

      {/* CARD 2: Datos de Contacto y Grupos */}
      <Card>
        <CardTextContainer style={{ flex: 1 }}>
          <CardText>
            <strong>Datos de Contacto</strong>
          </CardText>
          <CardText>
            <> <strong>Dirección:</strong> </>{patientData.dir || 'No registrada'}
            {hasPermission(PATHS.VER_DIRECCION) && (
              <img
                src={icons["pencil"]}
                alt="Editar dirección"
                style={{ width: 12, height: 12, marginLeft: "6px", cursor: "pointer", verticalAlign: "middle" }}
                className="hover"
                onClick={() => openModal("address")}
              />
            )}
          </CardText>
          <CardText>
            <> <strong>Teléfono:</strong> </> {patientData.tel || 'No registrado'}
            {hasPermission(PATHS.VER_TELEFONO) && (
              <img
                src={icons["pencil"]}
                alt="Editar teléfono"
                style={{ width: 12, height: 12, marginLeft: "6px", cursor: "pointer", verticalAlign: "middle" }}
                className="hover"
                onClick={() => openModal("phone")}
              />
            )}
          </CardText>
          <CardText>
            <> <strong>Correo:</strong> </> {patientData.correo || 'No registrado'}
            {hasPermission(PATHS.VER_CORREO) && (
              <img
                src={icons["pencil"]}
                alt="Editar correo"
                style={{
                  width: 12,
                  height: 12,
                  marginLeft: "6px",
                  cursor: "pointer",
                  verticalAlign: "middle"
                }}
                className="hover"
                onClick={() => openModal("mail")}
              />
            )}
          </CardText>
        </CardTextContainer>

        <CardTextContainer style={{ flex: 1, minWidth: "150px" }}>
          <CardText>
            <strong>Grupos de Riesgo</strong>
            {'  '}
            <img
              src={icons["addUser"]}
              alt="Gestionar Grupos"
              title="Gestionar Grupos del Paciente"
              style={{
                width: 18,
                height: 18,
                cursor: "pointer",
                verticalAlign: "middle",
                marginLeft: "12px",
              }}
              className="hover"
              onClick={() => openModal("activeGroup")}
            />
          </CardText>

          {GruposData.length === 0 ? (
            <CardText style={{ fontStyle: "italic", color: "#999" }}>
              Sin grupos asignados
            </CardText>
          ) : (
            <ScrollableGroupList>
              {GruposData.map((grupo, i) => {
                const subgruposTexto =
                  grupo.subgrupos?.length > 0
                    ? `${grupo.subgrupos.join(", ")}`
                    : null

                return (
                  <CardText key={grupo?.id || `grupo-${i}`}>
                    <span style={{ marginRight: 6, color: "#28a745" }}>●</span>

                    {/* Grupo activo */}
                    {grupo.activo === '1' && (
                      <>
                        {grupo.grupo}{'. '}
                        {subgruposTexto && (
                          <span style={{ fontSize: "0.9em", marginLeft: 4 }}>
                            {subgruposTexto}
                          </span>
                        )}
                      </>
                    )}

                    {/* Grupo inactivo → solo subgrupos */}
                    {grupo.activo === '0' && subgruposTexto && (
                      <span style={{ fontSize: "0.9em" }}>
                        {subgruposTexto}
                      </span>
                    )}
                  </CardText>
                )
              })}
            </ScrollableGroupList>
          )}
        </CardTextContainer>
      </Card>
    </CardsRow>
  );
};

export default PatientInfoCards;